import * as React from 'react';
import {ChevronDown} from 'lucide-react';
import {cn} from '../../lib/cn';

type SelectTriggerProps = {
  triggerRef?: React.Ref<HTMLButtonElement>;
  triggerId: string;
  listboxId: string;
  labelId: string;
  valueId: string;
  describedErrorId?: string;
  accessibleLabel?: string;
  hasValue: boolean;
  isOpen: boolean;
  isBlocked: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  loading?: boolean;
  invalid: boolean;
  placeholder?: string;
  showPlaceholderWhenIdle?: boolean;
  valueLabel?: string;
  valueContent?: React.ReactNode;
  leftIcon?: React.ReactNode;
  hideIndicator?: boolean;
  onClick: () => void;
  onKeyDown: React.KeyboardEventHandler<HTMLButtonElement>;
  onBlur?: React.FocusEventHandler<HTMLButtonElement>;
  onFocus?: React.FocusEventHandler<HTMLButtonElement>;
};

function SelectTrigger({
  triggerRef,
  triggerId,
  listboxId,
  labelId,
  valueId,
  describedErrorId,
  accessibleLabel,
  hasValue,
  isOpen,
  isBlocked,
  disabled,
  readOnly,
  loading,
  invalid,
  placeholder,
  showPlaceholderWhenIdle,
  valueLabel,
  valueContent,
  leftIcon,
  hideIndicator,
  onClick,
  onKeyDown,
  onBlur,
  onFocus,
}: SelectTriggerProps) {
  const isEmpty = !hasValue;
  return (
    <button
      id={triggerId}
      ref={triggerRef}
      type="button"
      aria-haspopup="listbox"
      aria-expanded={isOpen}
      aria-controls={listboxId}
      aria-label={accessibleLabel}
      aria-labelledby={
        accessibleLabel
          ? undefined
          : hasValue && valueId
            ? `${labelId} ${valueId}`
            : labelId
      }
      aria-describedby={describedErrorId}
      aria-invalid={invalid}
      aria-busy={loading}
      disabled={isBlocked}
      onClick={onClick}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      onFocus={onFocus}
      className={cn(
        'relative m-0 box-border flex h-12 w-full cursor-pointer items-center justify-between gap-2 rounded-[6px] border-0 pr-4 text-left text-[16px] font-medium leading-5 outline-none transition-colors duration-200',
        leftIcon ? 'pl-10' : 'pl-4',
        isEmpty
          ? 'bg-[var(--empty-field-background)] text-[var(--chekin-color-gray-1)]'
          : 'bg-transparent text-[var(--chekin-color-brand-navy)]',
        readOnly && !disabled && !loading && 'cursor-default',
        disabled && !loading && 'cursor-not-allowed opacity-50',
        loading && '!cursor-progress',
      )}
    >
      {leftIcon && (
        <span className="pointer-events-none absolute left-0 top-0 flex h-full max-w-10 items-center justify-center text-[var(--chekin-color-gray-2)]">
          <span className="flex h-full w-10 items-center justify-center">{leftIcon}</span>
        </span>
      )}
      <span id={valueId} className="block min-w-0 flex-1 truncate text-left">
        {hasValue
          ? (valueContent ?? valueLabel)
          : showPlaceholderWhenIdle || isOpen
            ? placeholder
            : null}
      </span>
      <span className="flex items-center gap-2 text-[var(--chekin-color-gray-2)]">
        {!hideIndicator && (
          <ChevronDown
            size={16}
            className={cn(
              'pointer-events-none transition-transform duration-200',
              isOpen && 'rotate-180 text-[var(--chekin-color-brand-blue)]',
            )}
          />
        )}
      </span>
    </button>
  );
}

export {SelectTrigger};
export type {SelectTriggerProps};
